import SoftgamesSplitTestService from './SoftgamesSplitTestService';
import { SplitTestStatus } from './splitTestTypes';
import type { SplitTestConfig, SplitTestService } from './splitTestTypes';
import type { App } from '../App';

type SplitTestHandlerInitConfig = {
  playerId: string;
  gameSlug: string;
  activeSplitTests: Record<string, number>;
  supportedTests: SplitTestConfig[];
  sessionNumber: number;
  app: App;
};

export class SplitTestHandler {
  private static activeSplitTest: Record<string, number> | null = null;

  public static getDistributionForTest(testConfig: SplitTestConfig): number | null {
    if (!this.activeSplitTest) {
      throw new Error('SplitTestHandler not initialized');
    }

    const activeSplitTests = this.activeSplitTest;

    const { newUserTestSlug, returningUserTestSlug } = testConfig;

    if (newUserTestSlug && activeSplitTests[newUserTestSlug] !== undefined) {
      return activeSplitTests[newUserTestSlug];
    }

    if (returningUserTestSlug && activeSplitTests[returningUserTestSlug] !== undefined) {
      return activeSplitTests[returningUserTestSlug];
    }

    return null;
  }

  public async init(config: SplitTestHandlerInitConfig): Promise<Record<string, number>> {
    const { gameSlug, playerId } = config;

    const service = new SoftgamesSplitTestService(gameSlug, playerId);

    const updatedSplitTests = await this.rollOutTests(service, {
      ...config,
      activeSplitTests: await this.clearInactiveSplitTests(service, config),
    });

    SplitTestHandler.activeSplitTest = updatedSplitTests;

    this.callOnActiveHook(config.app, config.supportedTests, updatedSplitTests);

    return updatedSplitTests;
  }

  private callOnActiveHook(app: App, configs: SplitTestConfig[], enrolledSplitTests: Record<string, number>): void {
    Object.entries(enrolledSplitTests).forEach(([testSlug, distribution]) => {
      const testConfig = this.getConfigByTestSlug(configs, testSlug);
      testConfig?.onActive?.(app, distribution);
    });
  }

  private getConfigByTestSlug(configs: SplitTestConfig[], testSlug: string): SplitTestConfig | null {
    return (
      configs.find((config) => config.newUserTestSlug === testSlug || config.returningUserTestSlug === testSlug) ?? null
    );
  }

  private async clearInactiveSplitTests(
    service: SplitTestService,
    config: SplitTestHandlerInitConfig,
  ): Promise<Record<string, number>> {
    const { activeSplitTests, supportedTests } = config;

    const result = { ...activeSplitTests };

    await Promise.all(
      Object.keys(activeSplitTests).map(async (slug): Promise<void> => {
        const testConfig = this.getConfigByTestSlug(supportedTests, slug);

        if (!testConfig) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete result[slug];
          return;
        }

        if (testConfig.manualRollOut) return;

        const status = await service.getSplitTestStatus(slug);
        if (status === SplitTestStatus.Active || status === SplitTestStatus.Full) return;

        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete result[slug];
      }),
    );

    return result;
  }

  private getSplitTestSlug(test: SplitTestConfig, sessionNumber: number): string | null {
    if (sessionNumber === 1) return test.newUserTestSlug ?? null;
    if (sessionNumber >= 4) return test.returningUserTestSlug ?? null;
    return null;
  }

  private async rollOutTests(
    service: SplitTestService,
    config: SplitTestHandlerInitConfig,
  ): Promise<Record<string, number>> {
    const { supportedTests, sessionNumber, activeSplitTests, app } = config;

    const result = { ...activeSplitTests };

    // don't allow for user to be in multiple tests at once
    if (Object.keys(result).length > 0) return result;

    const testsToRollOut = supportedTests.filter((testConfig) => {
      if (testConfig.manualRollOut) return false;
      if (testConfig?.excludedLanguages?.includes(navigator.language)) {
        return false;
      }

      if (testConfig?.targetedLanguages && !testConfig?.targetedLanguages?.includes(navigator.language)) {
        return false;
      }

      return !!this.getSplitTestSlug(testConfig, sessionNumber);
    });

    for (let testIndex = 0; testIndex < testsToRollOut.length; testIndex += 1) {
      if (Object.keys(result).length > 0) break;

      const testConfig = testsToRollOut[testIndex];

      const slug = this.getSplitTestSlug(testConfig, sessionNumber);
      if (!slug) continue;

      if (testConfig.rollOutConditions && !testConfig.rollOutConditions(app)) continue;

      const distribution = await service.getDistribution(slug);
      if (distribution === null) continue;

      result[slug] = distribution;
    }

    return result;
  }
}
